import React, {FunctionComponent, useCallback, useEffect, useMemo, useState, useTransition, memo} from 'react';
import {Box, Breakpoint, Chip, IconButton, InputAdornment, MenuItem, Skeleton, Stack, TextField, Theme, Tooltip, Typography, useColorScheme, useMediaQuery} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import InfiniteScroll from 'react-infinite-scroller';
import useIsMounted from '../../../../../../../hooks/use-is-mounted';
import {DefaultPaginationInfo} from '../../../../../../../../core/constants/enums';
import axios, {CancelToken} from "axios";
import {AugmentedDashboardWidgetEntry, DashboardViewFilterSectionFilters, DashboardWidgetEntry, DashboardWidgetType} from "../../types";
import Grid2 from "@mui/material/Unstable_Grid2";
// @ts-ignore
import {ReactComponent as BarLightSvg} from "../../assets/bar-light.svg";
// @ts-ignore
import {ReactComponent as BarDarkSvg} from "../../assets/bar-dark.svg";
// @ts-ignore
import {ReactComponent as LineLightSvg} from "../../assets/lines-light.svg";
// @ts-ignore
import {ReactComponent as LineDarkSvg} from "../../assets/lines-dark.svg";
// @ts-ignore
import {ReactComponent as TableLightSvg} from "../../assets/table-light.svg";
// @ts-ignore
import {ReactComponent as TableDarkSvg} from "../../assets/table-dark.svg";
// @ts-ignore
import {ReactComponent as InfoLightSvg} from "../../assets/button-light.svg";
// @ts-ignore
import {ReactComponent as TreemapDarkSvg} from "../../assets/treemap-dark.svg";
// @ts-ignore
import {ReactComponent as TreemapLightSvg} from "../../assets/treemap-light.svg";
// @ts-ignore
import {ReactComponent as InfoDarkSvg} from "../../assets/button-dark.svg";
import LoadingLottieAnimation from "../../../../../../../components/app-specific/lottie-animations/loading";
import Api from "../../../../../../../../core/services/api/api";
import {DashboardWidgetConfig} from "../../dashboard-widget.config";
import Slider from "react-slick";
import classnames from "classnames";
import './index.scss';
import LazyImage from "../../../../../../../components/app-specific/lazy-image";
// @ts-ignore
import NotImplementedImage from "../../assets/not-implemented.webp";
import EditDashboardWidgetFiltersDialog from "../../dialogs/edit-dashboard-widget-filters";

interface AddDashboardWidgetDrawerProps {
    onClose?: () => void;
    onAdd: (template: DashboardWidgetEntry, filters?: DashboardViewFilterSectionFilters) => Promise<boolean | void>;
    mobileThreshold: Breakpoint;
    alwaysMobile: boolean;
    selectedDashboardWidgets: AugmentedDashboardWidgetEntry[],
}

const pageSize = DefaultPaginationInfo.pageSize;

const allCategoriesTitle = 'All Categories';

interface Template extends DashboardWidgetEntry {
    existingCount: number;
}

/**
 * A drawer component that allows users to browse and select dashboard component templates.
 * Features include:
 * - Search functionality
 * - Category filtering
 * - Infinite scroll loading
 * - Responsive design (mobile/desktop)
 * - Loading states and skeletons
 */
const AddDashboardWidgetSection: React.FC<AddDashboardWidgetDrawerProps> = ({onClose, onAdd, mobileThreshold, alwaysMobile, selectedDashboardWidgets}) => {
    const [inputValue, setInputValue] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [, startTransition] = useTransition();
    const [selectedCategory, setSelectedCategory] = useState<string>(allCategoriesTitle);
    const [_templates, setTemplates] = useState<Template[]>([]);
    const [loading, setLoading] = useState(false);
    const [adding, setAdding] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pendingTemplate, setPendingTemplate] = useState<Template | null>(null);
    const isMounted = useIsMounted();

    const categories = useMemo(() => {
        const result = _templates.reduce((set, template) => {
            set.add(template.category);
            return set;
        }, new Set<string>())
        return Array.from([allCategoriesTitle, ...result]);
    }, [_templates])

    /**
     * Filters templates to exclude those already added to the dashboard
     *
     * @returns Filtered array of templates that are not already in use
     */
    const templates = useMemo(() => _templates.map(e => ({
        ...e,
        existingCount: selectedDashboardWidgets.filter(s => s.id === e.id).length
    })), [_templates, selectedDashboardWidgets])

    /**
     * Filters templates based on search query and selected category
     *
     * @returns Filtered array of templates matching:
     * - Search query (title or description)
     * - Selected category (or all categories)
     */
    const filteredTemplates = useMemo(() => {
        return templates.filter(template => {
            const matchesSearch = template.title.toLowerCase().includes(searchQuery.toLowerCase()) || template.description?.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesCategory = selectedCategory === allCategoriesTitle || template.category === selectedCategory;
            return matchesSearch && matchesCategory;
        });
    }, [templates, searchQuery, selectedCategory]);

    /**
     * Calculates the visible templates based on current page and page size
     */
    const visibleTemplates = useMemo<Template[]>(() => {
        const end = ((currentPage - 1) * pageSize) + pageSize;
        return filteredTemplates.slice(0, end);
    }, [currentPage, filteredTemplates]);


    /**
     * Fetches component templates from the API
     *
     * @param cancelToken - Axios cancel token for request cancellation
     *
     * Flow:
     * 1. Sets loading state
     * 2. Makes API call to get templates
     * 3. Updates templates and visibleTemplates states
     * 4. Handles initial page of templates
     * 5. Updates loading states
     */
    const fetchTemplates = useCallback(async (cancelToken: CancelToken) => {
        setLoading(true);
        const response = await Api.getCustomDashboardsAllWidgets(cancelToken);
        if (!isMounted()) return;
        if (!response?.aborted) {
            setLoading(false);
        }
        if (response?.resultFlag) {
            setTemplates(response.data ?? []);
            setCurrentPage(1);
        }
    }, [isMounted]);

    /**
     * Determines if there are more templates to load
     *
     * @returns boolean indicating if visibleTemplates length is less than filteredTemplates length
     * Note: Uses useMemo to prevent unnecessary recalculations
     */
    const hasMoreToLoad = useMemo(() => visibleTemplates.length < filteredTemplates.length && !loading, [visibleTemplates.length, filteredTemplates.length, loading]);

    /**
     * Handles loading more templates when scrolling
     */
    const handleLoadMore = useCallback(() => setCurrentPage(prevState => prevState + 1), []);

    /**
     * Adds a selected widget template to the dashboard, or opens required filters first.
     */
    const onWidgetSelected = useCallback(async (template: Template) => {
        if (adding) return;
        const requirements = new DashboardWidgetConfig(template.id).forcedFilterRequirements;
        if (requirements.length) {
            setPendingTemplate(template);
            return;
        }
        setAdding(true);
        await onAdd(template);
        if (!isMounted()) return;
        setAdding(false);
    }, [adding, isMounted, onAdd]);

    const pendingRequirements = useMemo(
        () => pendingTemplate ? new DashboardWidgetConfig(pendingTemplate.id).forcedFilterRequirements : [],
        [pendingTemplate],
    );

    /**
     * Adds the pending widget after required filters are applied.
     */
    const savePendingWidgetFilters = useCallback(async (filters?: DashboardViewFilterSectionFilters) => {
        if (!pendingTemplate || !filters) {
            return false;
        }
        setAdding(true);
        const resultFlag = await onAdd(pendingTemplate, filters);
        if (!isMounted()) return false;
        setAdding(false);
        if (resultFlag !== false) {
            setPendingTemplate(null);
            return true;
        }
        return false;
    }, [isMounted, onAdd, pendingTemplate]);

    /**
     * Closes the required-filters dialog without adding the widget.
     */
    const closePendingFiltersDialog = useCallback(() => {
        if (adding) return;
        setPendingTemplate(null);
    }, [adding]);

    /**
     * Effect: Fetch Templates
     *
     * Triggers when:
     * - Drawer opens (open prop changes)
     * - fetchTemplates function reference changes
     *
     * Responsibilities:
     * 1. Sets up request cancellation
     * 2. Calls fetchTemplates
     * 3. Cleans up on unmount
     */
    useEffect(() => {
        const cancelTokenSource = axios.CancelToken.source();
        void fetchTemplates(cancelTokenSource.token);
        return () => cancelTokenSource.cancel();
    }, [fetchTemplates]);

    return (
        <>
            <Box sx={{width: 'min(500px, 100vw)', display: 'flex', flexDirection: 'column', padding: 2, height: '100%',}}>
                <Box sx={{paddingBottom: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                    <Typography variant="subtitle1" fontWeight={600}>
                        Add Widget
                    </Typography>
                    {
                        !!onClose &&
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon/>
                        </IconButton>
                    }
                </Box>
                <Grid2 container spacing={2} sx={{borderBottom: 1, borderColor: 'divider', backgroundColor: 'background.paper', margin: 0}}>
                    <Grid2 xs={12}>
                        <TextField
                            fullWidth
                            size="small"
                            label={"Search"}
                            placeholder="Search widgets..."
                            value={inputValue}
                            onChange={(e) => {
                                const value = e.target.value;
                                // Update the visible input immediately so typing feels instant
                                setInputValue(value);
                                // Defer the expensive filter re-computation
                                startTransition(() => {
                                    setSearchQuery(value);
                                    setCurrentPage(1);
                                });
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon/>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid2>
                    <Grid2 xs={12}>
                        <TextField
                            select
                            fullWidth
                            label={"Category"}
                            size="small"
                            value={selectedCategory}
                            onChange={(e) => {
                                setSelectedCategory(e.target.value);
                                setCurrentPage(1);
                            }}
                        >
                            {categories.map((category) => (
                                <MenuItem key={category} value={category}>
                                    {category.charAt(0).toUpperCase() + category.slice(1)}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid2>
                </Grid2>
                <Box sx={{flexGrow: 1, overflow: 'auto', padding: 1, position: 'relative'}}>
                    {/*@ts-ignore*/}
                    <InfiniteScroll
                        pageStart={1}
                        loadMore={handleLoadMore}
                        hasMore={hasMoreToLoad}
                        useWindow={false}
                        threshold={100}
                        style={{
                            ...(adding ? {pointerEvents: 'none', height: '100%'} : {}),
                        }}
                    >
                        <Grid2 container spacing={2}>
                            {
                                !loading &&
                                visibleTemplates.map((template) => (
                                    <Grid2 xs={6} key={template.id}>
                                        <DashboardWidgetCard
                                            template={template}
                                            onSelect={onWidgetSelected}
                                            mobileThreshold={mobileThreshold}
                                            alwaysMobile={alwaysMobile}
                                        />
                                    </Grid2>
                                ))
                            }
                            {
                                loading &&
                                Array(pageSize).fill(null).map((_, index) => (
                                    <Grid2 xs={6} key={index}>
                                        <Skeleton
                                            variant="rectangular"
                                            width={'100%'}
                                            height={200}
                                            sx={{borderRadius: 1}}
                                        />
                                    </Grid2>
                                ))
                            }
                            {
                                !loading && visibleTemplates.length === 0 &&
                                <Grid2 xs={12}>
                                    <Typography variant="body2" color="text.secondary" textAlign={'center'} marginBlock={20}>
                                        No widgets found that match your criteria.
                                    </Typography>
                                </Grid2>
                            }
                        </Grid2>
                    </InfiniteScroll>
                </Box>
                {
                    adding &&
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        opacity: 0.7,
                        backdropFilter: 'blur(10px)',
                        backgroundColor: 'background.tertiary',
                        zIndex: 1,
                    }}>
                        <Stack
                            sx={{position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)'}}
                            justifyContent="center"
                            alignItems="center"
                        >
                            <LoadingLottieAnimation width={'100%'}/>
                            <Typography variant="body2" color="text.primary" marginTop={1}>
                                Adding widget...
                            </Typography>
                        </Stack>
                    </Box>
                }
            </Box>
            <EditDashboardWidgetFiltersDialog
                open={Boolean(pendingTemplate)}
                mode="add"
                canEdit
                widgetTitle={pendingTemplate?.title}
                widgetParameters={pendingTemplate?.parameters?.map((parameter) => parameter.name)}
                widgetParameterTitles={Object.fromEntries((pendingTemplate?.parameters ?? []).map((parameter) => [parameter.name, parameter.title]))}
                forcedFilterRequirements={pendingRequirements}
                filters={{mergeWithParentFilters: true}}
                onSave={savePendingWidgetFilters}
                onClose={closePendingFiltersDialog}
            />
        </>
    );
};

export default AddDashboardWidgetSection;


interface DashboardWidgetCardIconProps {
    type: DashboardWidgetType;
}

const DashboardWidgetCardIcon = (props: DashboardWidgetCardIconProps) => {
    const {mode: muiMode, systemMode} = useColorScheme();

    const mode = useMemo(() => {
        switch (muiMode) {
            case "dark":
            case "light":
                return muiMode;
            default:
                switch (systemMode) {
                    case "dark":
                    case "light":
                        return systemMode;
                    default:
                        return 'light';
                }
        }
    }, [muiMode, systemMode])

    const icon = useMemo(() => {
        switch (props.type) {
            case DashboardWidgetType.table:
                if (mode === 'dark') {
                    return <TableDarkSvg/>;
                } else {
                    return <TableLightSvg/>;
                }
            case DashboardWidgetType.lineChart:
                if (mode === 'dark') {
                    return <LineDarkSvg/>;
                } else {
                    return <LineLightSvg/>;
                }
            case DashboardWidgetType.barChart:
                if (mode === 'dark') {
                    return <BarDarkSvg/>;
                } else {
                    return <BarLightSvg/>;
                }
            case DashboardWidgetType.infoBlock:
                if (mode === 'dark') {
                    return <InfoDarkSvg/>;
                } else {
                    return <InfoLightSvg/>;
                }
            case DashboardWidgetType.treeMap:
                if (mode === 'dark') {
                    return <TreemapDarkSvg/>;
                } else {
                    return <TreemapLightSvg/>;
                }
            // TODO: add support for other widget types
            default:
                return null;
        }
    }, [mode, props.type]);

    return (
        <>
            <Box
                sx={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'cover',
                    objectPosition: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    svg: {
                        width: '100%',
                        height: 'auto',
                    },
                }}
            >
                {icon}
            </Box>
        </>
    );
}


interface DashboardWidgetCardProps {
    template: Template;
    onSelect: (template: Template) => void;
    mobileThreshold: Breakpoint;
    alwaysMobile: boolean;
}

const DashboardWidgetCard: React.FC<DashboardWidgetCardProps> = memo(({template, onSelect, mobileThreshold, alwaysMobile}) => {

    const compatibleTypes = useMemo(() => {
        const config = new DashboardWidgetConfig(template.id);
        const types = config.compatibleTypes.map(e => <DashboardWidgetCardIcon type={e}/>);
        if (types.length === 0) {
            types.push(
                <LazyImage
                    wrapperClassName={'mx-auto d-flex'}
                    src={NotImplementedImage}
                    onContextMenu={(e) => e.preventDefault()}
                    onDragStart={(e) => e.preventDefault()}
                    height={150 as any}
                    width={150 as any}
                />
            );
        }
        return types;
    }, [template.id]);

    return (
        <Box
            onClick={() => onSelect(template)}
            sx={(theme) => ({
                padding: 1,
                border: 1,
                borderRadius: 1,
                gap: 0.5,
                borderColor: theme.palette['app-specific']?.['card-border-color'],
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                height: '100%',
                position: 'relative',
            })}
        >
            <Box sx={{position: 'absolute', top: 0, right: 0, padding: 0.5, zIndex: 1}}>
                {
                    template.existingCount > 0 &&
                    <Chip
                        label={`${template.existingCount} in use`}
                        size="small"
                        color="primary"
                        variant="filled"
                        sx={{pointerEvents: 'none', fontWeight: 600}}
                    />
                }
            </Box>
            <Box sx={{marginInline: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%'}}>
                <StackedWidgets components={compatibleTypes} alwaysMobile={alwaysMobile} mobileThreshold={mobileThreshold}/>
            </Box>
            <Typography variant="body2" fontWeight={500}>
                {template.title}
            </Typography>
            <Typography variant="caption" fontWeight={500} color={'tertiary.main'} marginBlockEnd={0.5}>
                {template.category ?? ""}
            </Typography>
            <Tooltip title={
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                    {
                        template.parameters.map((param) => (
                            <Chip
                                key={param.name}
                                label={param.title}
                                size="small"
                                variant="filled"
                                color={'primary'}
                            />
                        ))
                    }
                </Stack>
            } followCursor arrow>
                <Typography variant="caption" color="secondary.main" sx={{mb: 0.5, width: '100%', display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'space-between'}}>
                    Affected Data Sources: <Typography variant="caption" component={'span'} fontWeight={600}>{template.parameters.length}</Typography>
                </Typography>
            </Tooltip>

            <Typography variant="caption" color="text.secondary" sx={{mb: 0.5}}>
                {template.description}
            </Typography>
        </Box>
    );
}) as React.FC<DashboardWidgetCardProps>;

export interface StackedWidgetsProps {
    components: React.ReactElement[];
    containerHeight?: number;
    containerWidth?: number;
    rotationAngle?: number;
    xOffset?: number;
    yOffset?: number;
    initialXOffset?: number;
    initialYOffset?: number;
    mobileThreshold: Breakpoint;
    alwaysMobile: boolean;
}

const StackedWidgets: FunctionComponent<StackedWidgetsProps> = ({components, mobileThreshold}) => {
    const [activeIndex, setActiveIndex] = React.useState(0);
    const sliderRef = React.useRef<any>(null);

    const inMobile = useMediaQuery<Theme>(theme => theme.breakpoints.down(mobileThreshold));

    /**
     * Modifies the event to prevent default behavior and stop propagation
     * @param e
     */
    const modifyEvent = useCallback((e: React.SyntheticEvent) => {
        e.stopPropagation();
        e.preventDefault();
    }, []);

    return (
        <Stack direction={'column'} alignItems={'center'} className={'dashboard-add-widgets'} onDragStart={modifyEvent}>
            {/*@ts-ignore*/}
            <Slider
                ref={sliderRef}
                autoplay
                infinite
                arrows={false}
                adaptiveHeight
                initialSlide={0}
                slidesToShow={1}
                variableWidth
                className={classnames("dashboard-add-widgets-slider", {'in-mobile': inMobile})}
                afterChange={(index) => setActiveIndex(index)}
            >
                {
                    components.map((component, index) => (
                        React.cloneElement(component, {key: index})
                    ))
                }
            </Slider>
            {
                components.length > 1 &&
                <div className={'custom-dots'} onClick={modifyEvent}>
                    {
                        components.map((item, index) => (
                            <div
                                key={index}
                                className={classnames(
                                    'dot',
                                    {
                                        'active': index === activeIndex,
                                        'active-minus-one': (index === activeIndex + 1) || (index === activeIndex - 1),
                                        'active-minus-two': (index === activeIndex + 2) || (index === activeIndex - 2),
                                    }
                                )}
                                onClick={(e) => {
                                    modifyEvent(e);
                                    sliderRef.current?.slickGoTo(index);
                                }}
                            />
                        ))
                    }
                </div>
            }
        </Stack>
    );
};